import { useQuery } from '@tanstack/react-query';
import { Activity, CircleX, Hourglass, Loader2, Pause, Play, RotateCw } from 'lucide-react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { TablePager } from '@/components/SortableTable';
import { Badge, Button, Card, CardContent, CardHeader, Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui';
import {
  pipelineApi,
  type AgentRunStatus,
  type AgentStatus,
  type InFlightDocument,
  type PipelineRun,
} from '@/lib/api';
import { cn } from '@/lib/cn';
import { useTableState } from '@/lib/use-table-state';

import { DRCDrawer } from './DRCDrawer';

// Pulled in one shot from the audit store; tables sort+paginate client-side.
// Bump this if the audit grows past a few thousand records.
const SERVER_FETCH_LIMIT = 200;
const POLL_INTERVAL_MS = 5_000;

type RunColumn = 'source' | 'timestamp' | 'finalStatus';

const RUN_COLUMNS = [
  { key: 'source' as const, accessor: (r: PipelineRun) => r.source_url || r.source_id },
  { key: 'timestamp' as const, accessor: (r: PipelineRun) => r.run_timestamp },
  { key: 'finalStatus' as const, accessor: (r: PipelineRun) => r.final_status ?? '' },
];

const STATUS_FILTERS: { value: string; labelKey: string }[] = [
  { value: '', labelKey: 'supervision.filters.all' },
  { value: 'staged', labelKey: 'supervision.filters.staged' },
  { value: 'staged_with_errors', labelKey: 'supervision.filters.stagedWithErrors' },
  { value: 'halted_layer2', labelKey: 'supervision.filters.halted' },
];

/**
 * Studio supervision view — per-document, per-agent run status.
 *
 * Pulls recent DRCs from Elasticsearch via `/api/v1/pipeline/runs`. The
 * default polling interval is 5 s so analysts can watch a batch progress
 * without manually refreshing; the Pause toggle stops the refetch when
 * they need a stable snapshot.
 */
export function PipelineRuns() {
  const { t } = useTranslation(['studio', 'common']);
  const [statusFilter, setStatusFilter] = useState<string>('');
  const [livePolling, setLivePolling] = useState(true);
  const [selectedKey, setSelectedKey] = useState<string | null>(null);
  const [drawerRun, setDrawerRun] = useState<PipelineRun | null>(null);

  const { data, isLoading, isFetching, dataUpdatedAt } = useQuery({
    queryKey: ['supervision', 'runs', statusFilter],
    queryFn: () =>
      pipelineApi.runs({
        final_status: statusFilter || undefined,
        limit: SERVER_FETCH_LIMIT,
        offset: 0,
      }),
    refetchInterval: livePolling ? POLL_INTERVAL_MS : false,
  });

  // In-flight = docs ingested but not yet completed Layer 2.
  // Polled on the same cadence so the panel stays in sync with the
  // table below as docs flip from in-flight → completed.
  const { data: inFlight } = useQuery({
    queryKey: ['supervision', 'in-flight'],
    queryFn: () => pipelineApi.inFlight(20),
    refetchInterval: livePolling ? POLL_INTERVAL_MS : false,
  });

  const tableState = useTableState<PipelineRun, RunColumn>({
    rows: data?.runs ?? [],
    columns: RUN_COLUMNS,
    initialSort: { by: 'timestamp', dir: 'desc' },
  });

  return (
    <div className="flex h-[calc(100vh-3.5rem)] flex-col p-3">
      <header className="mb-3 flex flex-wrap items-center gap-3">
        <div className="flex items-center gap-2 text-h2 text-brand-900">
          <Activity className="h-5 w-5 text-accent" aria-hidden />
          {t('supervision.title')}
        </div>

        <div className="ml-3 flex items-center gap-1 rounded border border-slate-200 p-0.5 text-small">
          {STATUS_FILTERS.map((f) => (
            <button
              key={f.value || 'all'}
              type="button"
              onClick={() => {
                setStatusFilter(f.value);
                tableState.setPageIndex(0);
              }}
              className={cn(
                'rounded px-2 py-1 transition',
                statusFilter === f.value
                  ? 'bg-brand-100 text-brand-800'
                  : 'text-slate-500 hover:text-slate-700',
              )}
            >
              {t(`studio:${f.labelKey}`)}
            </button>
          ))}
        </div>

        <div className="flex-1" />

        <span className="text-small text-slate-500">
          {data
            ? t('studio:supervision.runsTotal', { count: tableState.total })
            : t('common:status.loading')}
        </span>

        <Button
          type="button"
          variant="ghost"
          size="sm"
          onClick={() => setLivePolling((v) => !v)}
          aria-label={livePolling ? t('studio:supervision.pause') : t('studio:supervision.resume')}
        >
          {livePolling ? (
            <Pause className="mr-1 h-4 w-4" aria-hidden />
          ) : (
            <Play className="mr-1 h-4 w-4" aria-hidden />
          )}
          {livePolling ? t('studio:supervision.live') : t('studio:supervision.paused')}
          {isFetching && livePolling && (
            <RotateCw className="ml-1 h-3 w-3 animate-spin text-emerald-600" aria-hidden />
          )}
        </Button>
      </header>

      {inFlight && inFlight.total > 0 && (
        <InFlightPanel data={inFlight.items} total={inFlight.total} />
      )}

      <div className="grid h-full min-h-0 grid-cols-1 gap-3">
        <Card className="flex flex-col overflow-hidden">
          <CardHeader className="border-b border-slate-200 px-3 py-2">
            <div className="grid grid-cols-[2fr_140px_120px_repeat(6,_84px)_24px] items-center gap-2 text-h4 text-slate-600">
              <button
                type="button"
                onClick={() => tableState.toggleSort('source')}
                className={cn(
                  'text-left hover:text-slate-900',
                  tableState.sort?.by === 'source' && 'text-brand-900',
                )}
              >
                {t('supervision.cols.source')} {sortArrow(tableState, 'source')}
              </button>
              <button
                type="button"
                onClick={() => tableState.toggleSort('timestamp')}
                className={cn(
                  'text-left hover:text-slate-900',
                  tableState.sort?.by === 'timestamp' && 'text-brand-900',
                )}
              >
                {t('supervision.cols.timestamp')} {sortArrow(tableState, 'timestamp')}
              </button>
              <button
                type="button"
                onClick={() => tableState.toggleSort('finalStatus')}
                className={cn(
                  'text-left hover:text-slate-900',
                  tableState.sort?.by === 'finalStatus' && 'text-brand-900',
                )}
              >
                {t('supervision.cols.finalStatus')} {sortArrow(tableState, 'finalStatus')}
              </button>
              {data?.runs[0]?.agents.map((a) => (
                <Tooltip key={a.key}>
                  <TooltipTrigger asChild>
                    <span className="truncate text-center text-small">{a.label.split(' ')[0]}</span>
                  </TooltipTrigger>
                  <TooltipContent>{a.label}</TooltipContent>
                </Tooltip>
              ))}
              <span />
            </div>
          </CardHeader>
          <CardContent className="flex-1 overflow-auto p-0">
            {isLoading && (
              <div className="flex items-center gap-2 p-4 text-small text-slate-500">
                <Loader2 className="h-4 w-4 animate-spin" aria-hidden />
                {t('common:status.loading')}
              </div>
            )}
            {data && tableState.total === 0 && (
              <p className="p-6 text-center text-small text-slate-500">
                {t('supervision.empty')}
              </p>
            )}
            <ul className="divide-y divide-slate-100">
              {tableState.page.map((run) => (
                <RunRow
                  key={`${run.source_id}:${run.run_id}`}
                  run={run}
                  isSelected={selectedKey === `${run.source_id}:${run.run_id}`}
                  onSelect={() =>
                    setSelectedKey((prev) =>
                      prev === `${run.source_id}:${run.run_id}`
                        ? null
                        : `${run.source_id}:${run.run_id}`,
                    )
                  }
                  onOpenDrawer={() => setDrawerRun(run)}
                />
              ))}
            </ul>
          </CardContent>

          <div className="border-t border-slate-200">
            <TablePager
              pageIndex={tableState.pageIndex}
              pageCount={tableState.pageCount}
              pageSize={tableState.pageSize}
              total={tableState.total}
              onPageChange={tableState.setPageIndex}
              onPageSizeChange={tableState.setPageSize}
            />
          </div>
          {dataUpdatedAt && (
            <div className="border-t border-slate-100 px-3 py-1 text-small text-slate-500">
              {t('supervision.updatedAt', {
                time: new Date(dataUpdatedAt).toLocaleTimeString(),
              })}
            </div>
          )}
        </Card>
      </div>

      <DRCDrawer run={drawerRun} onClose={() => setDrawerRun(null)} />
    </div>
  );
}

function sortArrow(
  state: ReturnType<typeof useTableState<PipelineRun, RunColumn>>,
  key: RunColumn,
): string {
  if (state.sort?.by !== key) return '';
  return state.sort.dir === 'asc' ? '↑' : '↓';
}

function RunRow({
  run,
  isSelected,
  onSelect,
  onOpenDrawer,
}: {
  run: PipelineRun;
  isSelected: boolean;
  onSelect: () => void;
  onOpenDrawer: () => void;
}) {
  const { t } = useTranslation('studio');
  return (
    <li>
      <button
        type="button"
        onClick={onSelect}
        className={cn(
          'grid w-full grid-cols-[2fr_140px_120px_repeat(6,_84px)_24px] items-center gap-2 px-3 py-2 text-left text-small hover:bg-slate-50',
          isSelected && 'bg-brand-50/60',
        )}
      >
        <span className="truncate font-mono text-small text-slate-700" title={run.source_url}>
          {prettySource(run.source_url, run.source_id)}
        </span>
        <span className="text-slate-500" title={run.run_timestamp}>
          {prettyTime(run.run_timestamp)}
        </span>
        <FinalStatusBadge status={run.final_status} />
        {run.agents.map((a) => (
          <AgentCell key={a.key} agent={a} />
        ))}
        <span className="text-slate-300">{isSelected ? '▼' : '▸'}</span>
      </button>
      {isSelected && (
        <div className="bg-slate-50 px-4 py-3 text-small">
          <div className="grid grid-cols-2 gap-3">
            <div>
              <p className="text-h4 text-slate-700">{t('supervision.detail.sourceId')}</p>
              <p className="font-mono text-small text-slate-500">{run.source_id}</p>
            </div>
            <div>
              <p className="text-h4 text-slate-700">{t('supervision.detail.runId')}</p>
              <p className="font-mono text-small text-slate-500">{run.run_id}</p>
            </div>
            {run.halt_reason && (
              <div className="col-span-2">
                <p className="text-h4 text-red-700">{t('supervision.detail.haltReason')}</p>
                <p className="break-all font-mono text-small text-red-700">{run.halt_reason}</p>
              </div>
            )}
            {run.error_types.length > 0 && (
              <div className="col-span-2 flex items-center gap-2">
                <p className="text-h4 text-slate-700">{t('supervision.detail.errorTypes')}</p>
                {run.error_types.map((et) => (
                  <Badge key={et} variant="error">
                    {et}
                  </Badge>
                ))}
              </div>
            )}
            <div className="col-span-2">
              <p className="text-h4 text-slate-700">{t('supervision.detail.agentNotes')}</p>
              <ul className="mt-1 space-y-0.5 text-small">
                {run.agents.map((a) => (
                  <li key={a.key} className="grid grid-cols-[180px_1fr] gap-2">
                    <span className="text-slate-500">{a.label}</span>
                    <span className="text-slate-700">
                      {a.status === 'halted' && (
                        <span className="text-red-700">{a.halt_reason ?? 'halted'}</span>
                      )}
                      {a.status === 'done' && (a.notes ?? <span className="text-slate-400">—</span>)}
                      {a.status === 'not_run' && <span className="text-slate-400">—</span>}
                    </span>
                  </li>
                ))}
              </ul>
            </div>
            <div className="col-span-2 flex justify-end">
              <Button
                type="button"
                size="sm"
                variant="secondary"
                onClick={(e) => {
                  e.stopPropagation();
                  onOpenDrawer();
                }}
              >
                {t('supervision.drawer.openButton')}
              </Button>
            </div>
          </div>
        </div>
      )}
    </li>
  );
}

function AgentCell({ agent }: { agent: AgentStatus }) {
  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <span
          aria-label={agent.label}
          className={cn(
            'mx-auto inline-flex h-6 w-6 items-center justify-center rounded text-small font-medium',
            STATUS_CLASS[agent.status],
          )}
        >
          {STATUS_GLYPH[agent.status]}
        </span>
      </TooltipTrigger>
      <TooltipContent>
        <p>{agent.label}</p>
        {agent.notes && <p className="font-mono text-small">{agent.notes}</p>}
        {agent.halt_reason && (
          <p className="font-mono text-small text-red-200">{agent.halt_reason}</p>
        )}
      </TooltipContent>
    </Tooltip>
  );
}

function FinalStatusBadge({ status }: { status: string | null }) {
  if (!status) return <span className="text-slate-400">—</span>;
  const isHalt = status.includes('halt');
  const isStaged = status.includes('staged');
  if (isHalt) {
    return (
      <span className="inline-flex items-center gap-1 text-red-700">
        <CircleX className="h-3 w-3" aria-hidden />
        {status}
      </span>
    );
  }
  if (isStaged) {
    return <Badge variant="success">{status}</Badge>;
  }
  return <Badge variant="neutral">{status}</Badge>;
}

const STATUS_GLYPH: Record<AgentRunStatus, string> = {
  done: '✓',
  halted: '✗',
  not_run: '—',
};

const STATUS_CLASS: Record<AgentRunStatus, string> = {
  done: 'bg-emerald-100 text-emerald-700',
  halted: 'bg-red-100 text-red-700',
  not_run: 'bg-slate-100 text-slate-400',
};

function prettySource(url: string, sourceId: string): string {
  if (!url) return sourceId.slice(0, 8);
  if (url.startsWith('file://')) {
    const segments = url.split('/');
    return segments[segments.length - 1] || url;
  }
  return url.length > 60 ? `${url.slice(0, 57)}…` : url;
}

function prettyTime(iso: string): string {
  if (!iso) return '—';
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return iso;
  return date.toLocaleString();
}

// ─── In-flight panel ───────────────────────────────────────────

function InFlightPanel({
  data,
  total,
}: {
  data: InFlightDocument[];
  total: number;
}) {
  const { t } = useTranslation('studio');
  return (
    <Card className="mb-3 border-amber-200 bg-amber-50/50">
      <CardHeader className="pb-2">
        <div className="flex items-center gap-2">
          <Hourglass className="h-4 w-4 text-amber-600" aria-hidden />
          <h2 className="text-h3 text-brand-900">
            {t('supervision.inFlight.title', {
              count: total,
              defaultValue: '{{count}} document in flight',
              defaultValue_other: '{{count}} documents in flight',
            })}
          </h2>
        </div>
        <p className="text-small text-slate-600">
          {t(
            'supervision.inFlight.subtitle',
            'Ingested but not yet processed by Layer 2. Updates every 5 seconds — they will appear in the table below once their run completes or halts.',
          )}
        </p>
      </CardHeader>
      <CardContent className="pt-0">
        <ul className="divide-y divide-amber-200/50 text-small">
          {data.slice(0, 10).map((row) => (
            <li key={row.source_id} className="flex items-center gap-3 py-1">
              <span
                className="min-w-0 flex-1 truncate font-mono text-slate-700"
                title={row.source_url}
              >
                {prettySource(row.source_url, row.source_id)}
              </span>
              <Badge variant="neutral" className="text-[10px]">
                {row.source_type}
              </Badge>
              <span className="shrink-0 text-slate-500">
                {prettyWait(row.waiting_seconds)}
              </span>
            </li>
          ))}
        </ul>
        {total > 10 && (
          <p className="mt-1 text-small text-slate-500">
            +{total - 10} more
          </p>
        )}
      </CardContent>
    </Card>
  );
}

function prettyWait(seconds: number): string {
  if (seconds < 60) return `${seconds}s`;
  if (seconds < 3600) return `${Math.floor(seconds / 60)}m ${seconds % 60}s`;
  const h = Math.floor(seconds / 3600);
  const m = Math.floor((seconds % 3600) / 60);
  return `${h}h ${m}m`;
}
